/**
 * Hook for loading system_control templates from the automation_templates table.
 * Falls back to hardcoded CONTROL_TEMPLATES if DB fetch fails.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CONTROL_TEMPLATES, type ControlTemplate } from '@/lib/control-templates';
import type { SettingsChange } from '@/lib/control-types';

const QUERY_KEY = 'control-templates';

interface DbTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string | null;
  sort_order: number;
  is_recommended: boolean;
  settings_changes: Array<{ field: string; value: unknown }> | null;
  supported_providers: string[] | null;
  risk_level: string | null;
  coming_soon: boolean;
}

function dbToControlTemplate(t: DbTemplate): ControlTemplate {
  return {
    id: t.id,
    name: t.name,
    description: t.description,
    icon: t.icon ?? 'Settings2',
    category: t.category === 'system_control' ? 'system_control' : (t.category as ControlTemplate['category']),
    risk_level: (t.risk_level ?? 'low') as ControlTemplate['risk_level'],
    settings_changes: (t.settings_changes ?? []).map((s) => ({
      field: s.field,
      value: s.value,
    })) as SettingsChange[],
    supported_providers: t.supported_providers ?? [],
    coming_soon: t.coming_soon ?? false,
  };
}

async function fetchControlTemplates(): Promise<ControlTemplate[]> {
  const { data, error } = await supabase
    .from('automation_templates')
    .select('id, name, description, category, icon, sort_order, is_recommended, settings_changes, supported_providers, risk_level, coming_soon')
    .eq('category', 'system_control')
    .order('sort_order');

  if (error) {
    console.warn('[useControlTemplates] DB fetch failed, using hardcoded fallback:', error.message);
    return CONTROL_TEMPLATES;
  }

  if (!data || data.length === 0) {
    return CONTROL_TEMPLATES;
  }

  return (data as DbTemplate[]).map(dbToControlTemplate);
}

export function useControlTemplates() {
  const query = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: fetchControlTemplates,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    templates: query.data ?? CONTROL_TEMPLATES,
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** Get templates for a specific provider, grouped by category. */
export function filterTemplatesByProvider(
  templates: ControlTemplate[],
  providerType: string
): { active: ControlTemplate[]; comingSoon: ControlTemplate[] } {
  const forProvider = templates.filter(
    t => t.supported_providers.length === 0 || t.supported_providers.includes(providerType)
  );
  return {
    active: forProvider.filter(t => !t.coming_soon),
    comingSoon: forProvider.filter(t => t.coming_soon),
  };
}

/** Group active templates into quick_action and optimization/system_control. */
export function groupTemplatesByCategory(
  templates: ControlTemplate[]
): Record<string, ControlTemplate[]> {
  const grouped: Record<string, ControlTemplate[]> = {};
  for (const t of templates) {
    const cat = t.category === 'system_control' ? 'optimization' : t.category;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(t);
  }
  return grouped;
}
